import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar, Clock, MapPin, User, Phone, Mail,
  Coffee, DollarSign, ArrowLeft, Edit,
  CheckCircle, XCircle, AlertTriangle, FileText,
  PieChart, RefreshCw, BookMarked, Users, Printer, MessageSquare
} from 'lucide-react';
// Mock data removed - using only real Supabase data
import { MeetingStorage } from '../utils/meetingStorage';
import { meetingService } from '../utils/supabaseData';
import { supabase } from '../utils/supabase';
import CancellationDialog from '../components/CancellationDialog';
import { logger } from '../utils/logger';
import { savedOrdersService } from '../utils/savedOrdersService';
import SavedOrderIndicator from '../components/SavedOrderIndicator';
import AttendeeManagementModal from '../components/AttendeeManagementModal';
import { staffService } from '../utils/supabaseStaffService';
import PrintableAttendeeList from '../components/PrintableAttendeeList';
import { attendeeListService } from '../utils/attendeeListService';
import emailService from '../utils/emailService';

const SalesMeetingDetailView = ({ meetingId, setCurrentView, setSelectedMeetingId, currentUser }) => {
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancellationDialog, setShowCancellationDialog] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [actionStatus, setActionStatus] = useState('');
  const [discussionNotes, setDiscussionNotes] = useState('');
  const [followUpNotes, setFollowUpNotes] = useState('');
  const [saveInProgress, setSaveInProgress] = useState(false);

  // Attendee management state
  const [attendeeCount, setAttendeeCount] = useState(1);
  const [showAttendeeModal, setShowAttendeeModal] = useState(false);
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);

  // Saved orders state (read-only for sales reps)
  const [savedOrders, setSavedOrders] = useState([]);

  // Admin/office manager contact state
  const [primaryAdmin, setPrimaryAdmin] = useState(null);

  // Print attendee list state
  const [attendeeListData, setAttendeeListData] = useState(null);
  const [loadingAttendeeList, setLoadingAttendeeList] = useState(false);

  // Cost update state
  const [tempCost, setTempCost] = useState(null);

  // Load meeting data from Supabase for sales reps
  useEffect(() => {
    const loadMeeting = async () => {
      try {
        logger.debug('🔍 SalesMeetingDetailView: Loading meeting ID:', meetingId);

        // First try Supabase for sales reps
        if (currentUser && (currentUser.userType === 'sales' || currentUser.user_type === 'sales')) {
          try {
            // Get meeting by ID with full customer data from JOIN
            const supabaseMeeting = await meetingService.getMeetingById(meetingId);

            if (supabaseMeeting) {
              // Extract customer name with fallback logic
              let customerName = 'Healthcare Provider';

              if (supabaseMeeting.customer_name) {
                customerName = supabaseMeeting.customer_name;
              } else if (supabaseMeeting.customer && typeof supabaseMeeting.customer === 'object' && supabaseMeeting.customer.first_name) {
                customerName = `${supabaseMeeting.customer.first_name} ${supabaseMeeting.customer.last_name || ''}`.trim();
              } else if (supabaseMeeting.customer && typeof supabaseMeeting.customer === 'string' && supabaseMeeting.customer !== 'Healthcare Provider') {
                customerName = supabaseMeeting.customer;
              }

              // Always try direct fetch if we still have "Healthcare Provider" and have a customer_id
              if (customerName === 'Healthcare Provider' && supabaseMeeting.customer_id) {
                logger.debug('🔄 SalesMeetingDetailView: Attempting direct customer fetch for ID:', supabaseMeeting.customer_id);
                try {
                  const { data: customerData } = await supabase
                    .from('user_profiles')
                    .select('first_name, last_name')
                    .eq('id', supabaseMeeting.customer_id)
                    .single();

                  logger.debug('📋 SalesMeetingDetailView: Direct customer fetch result:', customerData);

                  if (customerData && customerData.first_name) {
                    customerName = `${customerData.first_name} ${customerData.last_name || ''}`.trim();
                    logger.debug('✅ SalesMeetingDetailView: Customer name from direct fetch:', customerName);
                  }
                } catch (fetchError) {
                  logger.error('❌ SalesMeetingDetailView: Could not fetch customer data directly:', fetchError);
                }
              }

              // Transform Supabase data to match UI expectations

              const transformedMeeting = {
                ...supabaseMeeting,
                date: supabaseMeeting.meeting_date,
                time: supabaseMeeting.start_time,
                salesRepId: supabaseMeeting.sales_rep_id,
                customerId: supabaseMeeting.customer_id,
                customer_name: customerName, // Keep string name for backwards compatibility
                // DON'T overwrite customer object - it has full data from JOIN
                restaurant: supabaseMeeting.restaurant_name || supabaseMeeting.restaurant || 'TBD',
                status: supabaseMeeting.status || 'pending',
                orderTotal: supabaseMeeting.actual_cost || supabaseMeeting.estimated_cost || '0.00',
                notes: supabaseMeeting.meeting_notes || supabaseMeeting.notes || ''
              };

              logger.debug('🔄 SalesMeetingDetailView: Transformed meeting:', {
                date: transformedMeeting.date,
                time: transformedMeeting.time,
                id: transformedMeeting.id
              });

              logger.debug('✅ SalesMeetingDetailView: Found Supabase meeting:', transformedMeeting);
              setMeeting(transformedMeeting);
              setDiscussionNotes(transformedMeeting.notes || '');
              setFollowUpNotes(transformedMeeting.followUp || transformedMeeting.follow_up_actions || '');
              setAttendeeCount(transformedMeeting.attendee_count || 2); // Default: 1 provider + 1 sales rep
              setSelectedStaffIds(transformedMeeting.selected_staff_ids || []);
              setLoading(false);
              return;
            }
          } catch (supabaseError) {
            logger.error('❌ SalesMeetingDetailView: Supabase error:', supabaseError);
          }
        }

        // Fallback to localStorage
        const storedMeeting = MeetingStorage.getMeetingById(meetingId);
        if (storedMeeting) {
          logger.debug('🔄 SalesMeetingDetailView: Using localStorage meeting');
          setMeeting(storedMeeting);
          setDiscussionNotes(storedMeeting.notes || '');
          setFollowUpNotes(storedMeeting.followUp || '');
          setAttendeeCount(storedMeeting.attendee_count || 2); // Default: 1 provider + 1 sales rep
          setSelectedStaffIds(storedMeeting.selected_staff_ids || []);
        } else {
          logger.warn('⚠️ SalesMeetingDetailView: Meeting not found in Supabase or localStorage');
          setMeeting(null);
        }
      } catch (error) {
        logger.error('❌ SalesMeetingDetailView: Error loading meeting:', error);
        logger.error('❌ Failed to load meeting:', error);
        setMeeting(null);
      } finally{
        setLoading(false);
      }
    };

    loadMeeting();
  }, [meetingId, currentUser]);

  // Load primary admin/office manager for the customer
  useEffect(() => {
    const loadPrimaryAdmin = async () => {
      if (!meeting || !meeting.customer_id) {
        return;
      }

      try {
        logger.debug('📋 Loading primary admin for customer:', meeting.customer_id);
        const admin = await staffService.getPrimaryAdmin(meeting.customer_id);
        setPrimaryAdmin(admin);
        logger.debug('✅ Loaded primary admin:', admin);
      } catch (error) {
        logger.error('❌ Error loading primary admin:', error);
        setPrimaryAdmin(null);
      }
    };

    loadPrimaryAdmin();
  }, [meeting?.customer_id]);

  // Load saved orders for this restaurant (customer's orders - read-only for sales rep)
  useEffect(() => {
    const loadSavedOrders = async () => {
      if (!meeting || !meeting.customer_id) {
        return;
      }

      const restaurantId = meeting.restaurant_yelp_id || meeting.restaurant_id || null;
      const restaurantName = meeting.restaurant_name || meeting.restaurant?.name;

      if (!restaurantName) {
        return;
      }

      try {
        // Use restaurant_yelp_id if available, otherwise fallback to restaurant name
        const searchKey = restaurantId || restaurantName;
        const orders = await savedOrdersService.getSavedOrdersByRestaurant(
          meeting.customer_id,
          searchKey
        );
        setSavedOrders(orders || []);
        logger.debug('Loaded customer saved orders for sales rep view', { count: orders?.length || 0 });
      } catch (error) {
        logger.error('Error loading customer saved orders', { error: error.message });
        setSavedOrders([]);
      }
    };

    loadSavedOrders();
  }, [meeting]);

  // Build customer object from Supabase meeting data
  const customer = useMemo(() => {
    if (!meeting) return null;

    logger.debug('👤 Building customer object from meeting:', {
      customer: meeting.customer,
      customer_type: typeof meeting.customer,
      customer_id: meeting.customer_id
    });

    // If customer is an object (from Supabase join), use it directly
    if (meeting.customer && typeof meeting.customer === 'object') {
      const customerObj = {
        name: `${meeting.customer.first_name || ''} ${meeting.customer.last_name || ''}`.trim() || 'N/A',
        email: meeting.customer.email || 'N/A',
        phone: meeting.customer.phone || 'N/A',
        company: meeting.customer.company || 'N/A'
      };

      logger.debug('👤 Built customer from meeting.customer object:', customerObj);
      return customerObj;
    }

    // Fallback: build from meeting fields
    const fallbackCustomer = {
      name: meeting.customer_name || meeting.customer || 'N/A',
      email: 'N/A',
      phone: 'N/A',
      company: 'N/A'
    };

    logger.debug('👤 Built customer from fallback (no customer object):', fallbackCustomer);
    return fallbackCustomer;
  }, [meeting]);

  // Build restaurant object from Supabase meeting data
  const restaurant = useMemo(() => {
    if (!meeting) return null;

    return {
      name: meeting.restaurant_name || meeting.restaurant?.name || 'TBD',
      address: meeting.restaurant_address || meeting.restaurant?.address,
      phone: meeting.restaurant_phone || meeting.restaurant?.phone,
      cuisineType: meeting.restaurant_cuisine_type || meeting.restaurant?.cuisineType,
      priceRange: meeting.restaurant_price_range || meeting.restaurant?.priceRange,
      rating: meeting.restaurant_rating || meeting.restaurant?.rating,
      url: meeting.restaurant_url || meeting.restaurant?.url
    };
  }, [meeting]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-green-600 bg-green-50';
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      case 'cancelled': return 'text-red-600 bg-red-50';
      case 'completed': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <CheckCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const handleCancelMeeting = async (cancellationData) => {
    try {
      logger.debug('🚫 Cancelling meeting:', { meetingId, cancellationData });

      // Check if meeting was loaded from Supabase (has Supabase-specific fields)
      const isSupabaseMeeting = meeting && (meeting.sales_rep_id || meeting.customer_id || meeting.meeting_date);

      if (isSupabaseMeeting) {
        // Cancel in Supabase
        const updateData = {
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          cancelled_by: cancellationData.cancelledBy,
          cancelled_by_name: cancellationData.cancelledByName,
          cancelled_by_type: cancellationData.cancelledByType,
          cancellation_reason: cancellationData.reason || '',
          cancellation_category: cancellationData.category || 'other',
          allow_reschedule: cancellationData.allowReschedule !== false,
          updated_at: new Date().toISOString()
        };

        logger.debug('📤 Updating meeting in Supabase:', updateData);

        try {
          const cancelledMeeting = await meetingService.updateMeeting(meetingId, updateData);

          if (cancelledMeeting) {
            logger.debug('✅ Meeting cancelled in Supabase successfully');
            setMeeting({
              ...meeting,
              status: 'cancelled',
              cancelled_at: updateData.cancelled_at,
              cancelled_by: updateData.cancelled_by,
              cancelled_by_name: updateData.cancelled_by_name,
              cancelled_by_type: updateData.cancelled_by_type,
              cancellation_reason: updateData.cancellation_reason,
              cancellation_category: updateData.cancellation_category,
              allow_reschedule: updateData.allow_reschedule
            });
            setShowCancellationDialog(false);

            // Send email notifications to customer and admins
            try {
              // Get all staff/admins for this customer
              const staffMembers = await staffService.getStaffForPhysician(meeting.customer_id);
              const adminEmails = staffMembers
                ?.filter(staff => staff.can_manage_meetings || staff.user_profiles?.email)
                ?.map(staff => staff.user_profiles?.email)
                ?.filter(Boolean) || [];

              logger.debug('📧 Sending cancellation emails to customer and admins', {
                customerEmail: customer?.email,
                adminEmails
              });

              // Format time for email (convert HH:MM:SS to HH:MM AM/PM)
              const formatTimeForEmail = (timeStr) => {
                if (!timeStr) return 'TBD';
                const timeParts = timeStr.split(':');
                const hours = parseInt(timeParts[0], 10);
                const minutes = parseInt(timeParts[1], 10);
                const ampm = hours >= 12 ? 'PM' : 'AM';
                const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
                return `${displayHour}:${minutes.toString().padStart(2, '0')} ${ampm}`;
              };

              // SAFE: Extract all names as strings to prevent React error #31
              // Pattern: Check type BEFORE using value (prevents rendering objects)
              const physicianNameStr = String(
                (customer?.name && typeof customer.name === 'string') ? customer.name :
                meeting.customer_name || 'Healthcare Provider'
              );

              const salesRepNameStr = String(
                (currentUser?.name && typeof currentUser.name === 'string') ? currentUser.name :
                (currentUser?.first_name && currentUser?.last_name) ? `${currentUser.first_name} ${currentUser.last_name}` :
                currentUser?.first_name || 'Sales Representative'
              );

              const restaurantNameStr = String(
                (restaurant?.name && typeof restaurant.name === 'string') ? restaurant.name :
                meeting.restaurant_name || 'TBD'
              );

              logger.debug('📧 Email notification parameters:', {
                physicianName: physicianNameStr,
                salesRepName: salesRepNameStr,
                restaurantName: restaurantNameStr
              });

              const emailResult = await emailService.sendMeetingCancellationNotification({
                physicianName: physicianNameStr,
                physicianEmail: customer?.email,
                adminEmails: adminEmails,
                salesRepName: salesRepNameStr,
                meetingDate: meeting.meeting_date || meeting.date,
                meetingTime: formatTimeForEmail(meeting.start_time || meeting.time),
                locationName: meeting.location_name || 'TBD',
                restaurantName: restaurantNameStr,
                cancellationReason: cancellationData.reason,
                cancellationCategory: cancellationData.category,
                allowReschedule: cancellationData.allowReschedule,
                suggestedDates: cancellationData.suggestedDates || []
              });

              if (emailResult.success) {
                logger.debug('✅ Cancellation emails sent successfully', emailResult.results);
                // SAFE: Use the already-validated physicianNameStr instead of re-extracting
                setActionStatus(`Meeting cancelled successfully. Notifications sent to ${physicianNameStr}${adminEmails.length > 0 ? ' and admins' : ''}.`);
              } else {
                logger.warn('⚠️ Some cancellation emails failed to send', emailResult.results);
                setActionStatus(`Meeting cancelled successfully. Note: Some email notifications may not have been delivered.`);
              }
            } catch (emailError) {
              logger.error('❌ Error sending cancellation emails:', emailError);
              setActionStatus('Meeting cancelled successfully. Note: Email notifications could not be sent.');
            }

            setTimeout(() => setActionStatus(''), 8000);
          } else {
            throw new Error('Supabase update returned null');
          }
        } catch (supabaseError) {
          logger.error('❌ Supabase update failed:', supabaseError);
          logger.debug('⚠️ Updating UI only - database update failed due to RLS policies');

          // Update UI even if database update fails (RLS policy issue)
          setMeeting({
            ...meeting,
            status: 'cancelled',
            cancelled_at: updateData.cancelled_at,
            cancellation_reason: updateData.cancellation_reason
          });
          setShowCancellationDialog(false);
          setActionStatus('Meeting cancelled in UI. Note: Database update failed - please check Supabase RLS policies.');
          setTimeout(() => setActionStatus(''), 8000);
        }
      } else {
        // Cancel in localStorage
        const cancelledMeeting = MeetingStorage.cancelMeeting(meetingId, cancellationData);
        if (cancelledMeeting) {
          logger.debug('✅ Meeting cancelled in localStorage successfully');
          setMeeting(cancelledMeeting);
          setShowCancellationDialog(false);
          setActionStatus('Meeting cancelled successfully. Notification sent to healthcare provider.');
          setTimeout(() => setActionStatus(''), 5000);
        } else {
          throw new Error('Failed to cancel meeting');
        }
      }
    } catch (error) {
      logger.error('❌ Error cancelling meeting:', error);
      setActionStatus(error.message || 'Error cancelling meeting. Please try again.');
      setTimeout(() => setActionStatus(''), 5000);
    }
  };

  const handleSaveNotes = async () => {
    setSaveInProgress(true);
    setActionStatus('');

    try {
      logger.debug('💾 Saving meeting notes:', {
        meetingId,
        discussionNotes,
        followUpNotes
      });

      // Try to save to Supabase first
      try {
        const updateData = {
          meeting_notes: discussionNotes,
          follow_up_actions: followUpNotes,
          updated_at: new Date().toISOString()
        };

        const updatedMeeting = await meetingService.updateMeeting(meetingId, updateData);

        if (updatedMeeting) {
          logger.debug('✅ Notes saved to Supabase successfully');
          setMeeting({
            ...meeting,
            notes: discussionNotes,
            followUp: followUpNotes,
            meeting_notes: discussionNotes,
            follow_up_actions: followUpNotes
          });
          setActionStatus('Notes saved successfully!');
          setTimeout(() => setActionStatus(''), 3000);
        }
      } catch (supabaseError) {
        logger.warn('⚠️ Supabase save failed, trying localStorage fallback:', supabaseError);

        // Fallback to localStorage
        const updatedMeeting = MeetingStorage.updateMeeting(meetingId, {
          notes: discussionNotes,
          followUp: followUpNotes
        });

        if (updatedMeeting) {
          logger.debug('✅ Notes saved to localStorage successfully');
          setMeeting(updatedMeeting);
          setActionStatus('Notes saved successfully!');
          setTimeout(() => setActionStatus(''), 3000);
        } else {
          throw new Error('Failed to save notes');
        }
      }
    } catch (error) {
      logger.error('❌ Error saving notes:', error);
      setActionStatus('Error saving notes. Please try again.');
      setTimeout(() => setActionStatus(''), 5000);
    } finally {
      setSaveInProgress(false);
    }
  };

  // Format cost for display (always show 2 decimal places)
  const formatCostForDisplay = (cost) => {
    if (cost === null || cost === undefined || cost === '') return '';
    const numCost = parseFloat(cost);
    return isNaN(numCost) ? '' : numCost.toFixed(2);
  };

  // Format currency with commas (e.g., 1234.56 -> "1,234.56")
  const formatCurrencyWithCommas = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '';
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };


  // Save cost to database on blur
  const saveCostToDatabase = async () => {
    if (tempCost === null) return;

    try {
      // Parse and format to 2 decimal places
      let costValue = parseFloat(tempCost) || 0;
      if (!isNaN(costValue)) {
        costValue = parseFloat(costValue.toFixed(2));
      }

      logger.debug('Saving actual cost to database', { meetingId, cost: costValue });

      const { error } = await supabase
        .from('meetings')
        .update({ actual_cost: costValue })
        .eq('id', meetingId);

      if (error) {
        throw error;
      }

      // Update local meeting state with formatted value
      setMeeting(prev => ({
        ...prev,
        actual_cost: costValue
      }));

      logger.debug('Cost saved successfully');
      setTempCost(null); // Reset temp state
    } catch (error) {
      logger.error('Error saving cost', { error: error.message, meetingId });
      setActionStatus('Error saving cost. Please try again.');
      setTimeout(() => setActionStatus(''), 3000);
    }
  };

  const handleSaveAttendees = async (newAttendeeCount, selectedStaff) => {
    setActionStatus('');

    try {
      logger.debug('Saving attendees', {
        meetingId,
        attendeeCount: newAttendeeCount,
        selectedStaffCount: selectedStaff.length
      });

      // Try to save to Supabase first
      try {
        const staffIds = selectedStaff.map(s => s.id);
        const updateData = {
          attendee_count: newAttendeeCount,
          selected_staff_ids: staffIds,
          updated_at: new Date().toISOString()
        };

        const updatedMeeting = await meetingService.updateMeeting(meetingId, updateData);

        if (updatedMeeting) {
          logger.debug('Attendee count saved to Supabase successfully');
          setMeeting({
            ...meeting,
            attendee_count: newAttendeeCount,
            selected_staff_ids: staffIds
          });
          setAttendeeCount(newAttendeeCount);
          setSelectedStaffIds(staffIds);
          setActionStatus('Attendees updated successfully!');
          setTimeout(() => setActionStatus(''), 3000);
        }
      } catch (supabaseError) {
        logger.warn('Supabase save failed, trying localStorage fallback', { error: supabaseError.message });

        // Fallback to localStorage
        const updatedMeeting = MeetingStorage.updateMeeting(meetingId, {
          attendee_count: newAttendeeCount
        });

        if (updatedMeeting) {
          logger.debug('Attendee count saved to localStorage successfully');
          setMeeting(updatedMeeting);
          setAttendeeCount(newAttendeeCount);
          setSelectedStaffIds(selectedStaff.map(s => s.id));
          setActionStatus('Attendees updated successfully!');
          setTimeout(() => setActionStatus(''), 3000);
        } else {
          throw new Error('Failed to save attendee count');
        }
      }
    } catch (error) {
      logger.error('Error saving attendees', { error: error.message });
      setActionStatus('Error saving attendees. Please try again.');
      setTimeout(() => setActionStatus(''), 5000);
    }
  };

  const handlePrintAttendeeList = async () => {
    try {
      setLoadingAttendeeList(true);
      logger.debug('Fetching attendee list for printing', { meetingId });

      const attendeeData = await attendeeListService.getAttendeeList(meetingId);
      setAttendeeListData(attendeeData);

      // Wait a moment for React to render the PrintableAttendeeList component
      setTimeout(() => {
        window.print();
        setLoadingAttendeeList(false);
      }, 100);
    } catch (error) {
      logger.error('Error loading attendee list for printing', { error: error.message });
      setActionStatus('Error loading attendee list. Please try again.');
      setLoadingAttendeeList(false);
      setTimeout(() => setActionStatus(''), 5000);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Loading meeting details...</span>
        </div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Meeting Not Found</h3>
          <p className="text-gray-600 mb-4">The requested meeting could not be found.</p>
          <button
            onClick={() => setCurrentView('meetings')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            Back to Meetings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => setCurrentView('meetings')}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Meetings
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Meeting Details</h2>
            <p className="text-gray-600 mt-1">
              Meeting with {meeting?.customer_name || (meeting?.customer?.first_name && meeting?.customer?.last_name && `${meeting.customer.first_name} ${meeting.customer.last_name}`) || 'Healthcare Provider'}
            </p>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1 ${getStatusColor(meeting.status)}`}>
            {getStatusIcon(meeting.status)}
            <span className="capitalize">{meeting.status}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Meeting Information */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Meeting Information</h3>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-700">Date</p>
                    <p className="text-gray-600">{meeting.date}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-700">Time</p>
                    <p className="text-gray-600">{meeting.time}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Users className="w-5 h-5 text-gray-400" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-700">Attendees</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-gray-600 font-semibold">{meeting.attendee_count || 2}</span>
                      <span className="text-gray-500 text-sm">{(meeting.attendee_count || 2) === 1 ? 'person' : 'people'}</span>
                      <button
                        onClick={() => setShowAttendeeModal(true)}
                        className="ml-1 p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors"
                        title="Manage attendees"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handlePrintAttendeeList}
                        disabled={loadingAttendeeList}
                        className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-full transition-colors disabled:opacity-50"
                        title="Print attendee list"
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                {/* Meeting Location (Practice Location) */}
                {meeting.location_name && (
                  <div className="flex items-start space-x-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-blue-900 mb-1">📍 Meeting Location</p>
                      <p className="text-gray-800 font-semibold">{meeting.location_name}</p>
                      {meeting.location_address && (
                        <p className="text-sm text-gray-600 mt-1">{meeting.location_address}</p>
                      )}
                      <p className="text-xs text-blue-700 mt-2 italic">
                        This is where the meeting will be held
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Booking Notes */}
            {(meeting.booking_notes || meeting.notes) && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-start space-x-3">
                  <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-700 mb-2">Booking Notes</p>
                    <p className="text-gray-600 text-sm whitespace-pre-wrap">
                      {meeting.booking_notes || meeting.notes}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Restaurant (Food Delivery) */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
              <MapPin className="w-5 h-5 text-orange-600 mr-2" />
              Restaurant
            </h3>
            <div className="space-y-2">
              <div>
                <p className="font-semibold text-gray-800 text-lg">{restaurant?.name || 'TBD'}</p>
              </div>
              {restaurant?.address && (
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-0.5">Address</p>
                  <a
                    href={`https://maps.apple.com/?q=${encodeURIComponent(restaurant.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {restaurant.address} →
                  </a>
                </div>
              )}
              {restaurant?.phone && (
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-0.5">Phone</p>
                  <a
                    href={`tel:${restaurant.phone.replace(/\D/g, '')}`}
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {restaurant.phone}
                  </a>
                </div>
              )}
              <div className="flex items-center gap-2 pt-1">
                {restaurant?.cuisineType && (
                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                    {restaurant.cuisineType}
                  </span>
                )}
                {restaurant?.priceRange && (
                  <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">
                    {restaurant.priceRange}
                  </span>
                )}
                {restaurant?.rating && (
                  <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded">
                    ⭐ {restaurant.rating}
                  </span>
                )}
              </div>
              {restaurant?.url && (
                <div className="pt-1">
                  <a
                    href={restaurant.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    View on Yelp →
                  </a>
                </div>
              )}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-2 mt-2">
                <p className="text-xs text-orange-700 italic">
                  Pick up food here, then deliver to meeting location
                </p>
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Customer</h3>
            {customer ? (
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800 text-lg">{customer.name}</p>
                    {customer.company && customer.company !== 'N/A' && (
                      <p className="text-sm text-gray-600 mt-1">{customer.company}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-3 mt-4">
                  {customer.email && customer.email !== 'N/A' && (
                    <div className="flex items-center space-x-3">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">{customer.email}</span>
                    </div>
                  )}
                  {customer.phone && customer.phone !== 'N/A' && (
                    <div className="flex items-center space-x-3">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <a
                        href={`tel:${customer.phone.replace(/\D/g, '')}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {customer.phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-500">Customer information not available</p>
            )}
          </div>

          {/* Admin/Office Manager Contact */}
          {primaryAdmin && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Office Contact</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800 text-lg">{primaryAdmin.name}</p>
                    {primaryAdmin.role && (
                      <p className="text-sm text-gray-600 mt-1">{primaryAdmin.role}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-3 mt-4">
                  {primaryAdmin.email && (
                    <div className="flex items-center space-x-3">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <a
                        href={`mailto:${primaryAdmin.email}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {primaryAdmin.email}
                      </a>
                    </div>
                  )}
                  {primaryAdmin.phone && (
                    <div className="flex items-center space-x-3">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <a
                        href={`tel:${primaryAdmin.phone.replace(/\D/g, '')}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {primaryAdmin.phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Customer's Saved Orders - Read-only for sales rep */}
          {restaurant && savedOrders.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Customer's Saved Orders</h3>
                <SavedOrderIndicator count={savedOrders.length} />
              </div>

              <p className="text-xs text-gray-500 mb-4">
                This customer has saved their typical orders for {restaurant.name}
              </p>

              <div className="space-y-3">
                {savedOrders.map((order) => (
                  <div key={order.id} className="border border-green-200 bg-green-50 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <BookMarked className="w-4 h-4 text-green-600" />
                        <h4 className="font-semibold text-green-900">{order.order_template_name}</h4>
                      </div>
                      {order.average_order_size && (
                        <span className="text-sm font-medium text-green-700">
                          Avg: ${order.average_order_size}
                        </span>
                      )}
                    </div>

                    <div className="mb-3">
                      <p className="text-sm text-green-800 whitespace-pre-wrap font-mono bg-white p-2 rounded border border-green-200">
                        {order.order_items}
                      </p>
                    </div>

                    {order.order_notes && (
                      <div className="mb-2 text-xs text-green-700 bg-green-100 rounded p-2">
                        <span className="font-medium">Special Instructions: </span>
                        {order.order_notes}
                      </div>
                    )}

                    {order.times_used > 0 && (
                      <p className="text-xs text-green-600">
                        Customer has used this order {order.times_used} time{order.times_used !== 1 ? 's' : ''}
                        {order.last_used && ` • Last: ${new Date(order.last_used).toLocaleDateString()}`}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  💡 <span className="font-medium">Tip:</span> Use this information to suggest the customer's usual order when booking future meetings at this restaurant.
                </p>
              </div>
            </div>
          )}

          {/* Order Details */}
          {meeting.orderItems && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Order Summary</h3>
              <div className="space-y-3">
                {meeting.orderItems.map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                    <div>
                      <p className="font-medium text-gray-800">{item.name}</p>
                      {item.modifications && (
                        <p className="text-sm text-gray-500">{item.modifications}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{item.quantity}x ${item.price}</p>
                    </div>
                  </div>
                ))}
                <div className="border-t pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Subtotal</span>
                    <span>${(parseFloat(meeting.orderTotal) - 3.99).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Delivery Fee</span>
                    <span className="text-gray-600">$3.99</span>
                  </div>
                  <div className="flex justify-between items-center font-semibold text-lg">
                    <span>Total</span>
                    <span>${meeting.orderTotal}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Meeting Stats</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Total Spend</span>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500">$</span>
                  <input
                    type="text"
                    value={tempCost !== null
                      ? tempCost
                      : formatCurrencyWithCommas(meeting.actual_cost)}
                    onFocus={() => {
                      // Set raw value when focusing
                      setTempCost(meeting.actual_cost ? meeting.actual_cost.toString() : '');
                    }}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      // Only allow valid number characters
                      if (rawValue === '' || /^\d*\.?\d{0,2}$/.test(rawValue)) {
                        setTempCost(rawValue);
                        // Update meeting state with raw value
                        setMeeting(prev => ({
                          ...prev,
                          actual_cost: rawValue
                        }));
                      }
                    }}
                    onBlur={() => {
                      saveCostToDatabase();
                      // Clear editing state
                      setTempCost(null);
                    }}
                    className="w-28 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right font-semibold"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Attendee Count</span>
                <span className="font-semibold">{meeting.attendee_count || 2}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Cost Per Attendee</span>
                <span className="font-semibold">
                  ${meeting.actual_cost && (meeting.attendee_count || 2) > 0
                    ? (parseFloat(meeting.actual_cost) / (meeting.attendee_count || 2)).toFixed(2)
                    : '0.00'}
                </span>
              </div>
            </div>
          </div>

          {/* Meeting Notes */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Meeting Notes</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discussion Topics
                </label>
                <textarea
                  value={discussionNotes}
                  onChange={(e) => setDiscussionNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  placeholder="Add notes about the meeting discussion..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Follow-up Actions
                </label>
                <textarea
                  value={followUpNotes}
                  onChange={(e) => setFollowUpNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Add any follow-up actions..."
                />
              </div>
              <button
                onClick={handleSaveNotes}
                disabled={saveInProgress}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors"
              >
                {saveInProgress ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Save Notes</span>
                )}
              </button>
            </div>
          </div>

          {/* Action Status */}
          {actionStatus && (
            <div className={`bg-white rounded-lg shadow-md p-4 border-l-4 ${
              actionStatus.includes('Error') || actionStatus.includes('Failed')
                ? 'border-red-500 bg-red-50'
                : 'border-green-500 bg-green-50'
            }`}>
              <div className="flex items-center space-x-2">
                {actionStatus.includes('Error') || actionStatus.includes('Failed') ? (
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                )}
                <span className={`text-sm font-medium ${
                  actionStatus.includes('Error') || actionStatus.includes('Failed')
                    ? 'text-red-700'
                    : 'text-green-700'
                }`}>
                  {actionStatus}
                </span>
              </div>
            </div>
          )}

          {/* Provider Response Message */}
          {meeting.response_message && (meeting.status === 'confirmed' || meeting.status === 'declined') && (
            <div className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${
              meeting.status === 'confirmed' ? 'border-green-500' : 'border-yellow-500'
            }`}>
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <MessageSquare className={`w-5 h-5 mr-2 ${
                  meeting.status === 'confirmed' ? 'text-green-600' : 'text-yellow-600'
                }`} />
                Message from Provider
              </h3>
              <div className={`p-4 rounded-lg ${
                meeting.status === 'confirmed' ? 'bg-green-50' : 'bg-yellow-50'
              }`}>
                <p className="text-gray-700 whitespace-pre-wrap">{meeting.response_message}</p>
              </div>
              {meeting.confirmed_at && meeting.status === 'confirmed' && (
                <p className="text-xs text-gray-500 mt-2">
                  Confirmed on {new Date(meeting.confirmed_at).toLocaleDateString()} at {new Date(meeting.confirmed_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </p>
              )}
              {meeting.declined_at && meeting.status === 'declined' && (
                <p className="text-xs text-gray-500 mt-2">
                  Declined on {new Date(meeting.declined_at).toLocaleDateString()} at {new Date(meeting.declined_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </p>
              )}
            </div>
          )}

          {/* Cancellation Information */}
          {meeting.status === 'cancelled' && (
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <XCircle className="w-5 h-5 text-red-600 mr-2" />
                Meeting Cancelled
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700">Cancelled by:</p>
                  <p className="text-gray-600">
                    {meeting.cancelled_by_type === 'sales' ? 'You' : (meeting.cancelled_by_name || meeting?.customer || 'Healthcare Provider')}
                    {meeting.cancelled_at && (
                      <span className="text-sm text-gray-500 ml-2">
                        on {new Date(meeting.cancelled_at).toLocaleDateString()}
                      </span>
                    )}
                  </p>
                </div>
                {meeting.cancellationReason && (
                  <div>
                    <p className="text-sm font-medium text-gray-700">Reason:</p>
                    <p className="text-gray-600">{meeting.cancellationReason}</p>
                  </div>
                )}
                {meeting.cancellationCategory && meeting.cancellationCategory !== 'other' && (
                  <div>
                    <p className="text-sm font-medium text-gray-700">Category:</p>
                    <p className="text-gray-600 capitalize">{meeting.cancellationCategory.replace('_', ' ')}</p>
                  </div>
                )}
                {meeting.allowReschedule && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                    <p className="text-blue-800 text-sm">
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      Rescheduling is allowed for this meeting.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Status Actions */}
          {meeting.status !== 'cancelled' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={handlePrintAttendeeList}
                  disabled={loadingAttendeeList}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  {loadingAttendeeList ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <Printer className="w-4 h-4" />
                      <span>Print Attendee List</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    if (setSelectedMeetingId) {
                      setSelectedMeetingId(meetingId);
                    }
                    setCurrentView('edit-meeting');
                  }}
                  disabled={actionInProgress}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  <span>Edit Meeting</span>
                </button>
                {(meeting.status === 'pending' || meeting.status === 'confirmed') && (
                  <button
                    onClick={() => setShowCancellationDialog(true)}
                    disabled={actionInProgress}
                    className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                  >
                    <XCircle className="w-4 h-4" />
                    <span>Cancel Meeting</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Compliance */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Compliance
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Fair Market Value</span>
                <span className="text-green-600 font-medium">✓ Compliant</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Documentation</span>
                <span className="text-green-600 font-medium">✓ Complete</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Reporting Status</span>
                <span className="text-yellow-600 font-medium">⚠ Pending</span>
              </div>
              <button
                onClick={() => setCurrentView('reports')}
                className="w-full text-blue-600 hover:text-blue-800 text-sm font-medium mt-3"
              >
                Generate Compliance Report →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Cancellation Dialog */}
      <CancellationDialog
        isOpen={showCancellationDialog}
        onClose={() => setShowCancellationDialog(false)}
        onConfirm={handleCancelMeeting}
        meeting={meeting}
        userType="sales"
        currentUser={currentUser}
      />

      {/* Attendee Management Modal */}
      <AttendeeManagementModal
        isOpen={showAttendeeModal}
        onClose={() => setShowAttendeeModal(false)}
        customerId={meeting?.customer_id}
        meetingId={meetingId}
        initialSelectedStaff={selectedStaffIds}
        onSave={handleSaveAttendees}
      />

      {/* Printable Attendee List (hidden on screen, visible when printing) */}
      {attendeeListData && (
        <PrintableAttendeeList attendeeData={attendeeListData} />
      )}
    </div>
  );
};

export default SalesMeetingDetailView;